import type { GameId, PlayerPublic } from '@xox/shared'
import { KeyRound, Menu } from 'lucide-react'
import type { ReactNode } from 'react'
import { BOSS_THEME } from '../lib/bossTheme'
import { feedback } from '../lib/feedback'
import { RobotFace, robotPaletteFor } from './art/RobotFace'

export interface NavTab {
  key: string
  label: string
  icon: ReactNode
  onSelect: () => void
  active?: boolean
  disabled?: boolean
  /** A house bot's tab glows in its own colours. */
  tone?: 'boss' | 'drop'
}

/** A tab that opens one of a page's sheets and lights up while that sheet is open. */
export function sheetTab<S extends string>(
  key: S,
  label: string,
  icon: ReactNode,
  sheet: string | null,
  open: (s: S) => void,
): NavTab {
  return { key, label, icon, onSelect: () => open(key), active: sheet === key }
}

/** The Join and Menu tabs every game home shares. */
export function joinTab(sheet: string | null, open: (s: 'join') => void): NavTab {
  return sheetTab('join', 'Join', <KeyRound className="size-5" aria-hidden />, sheet, open)
}
export function menuTab(sheet: string | null, open: (s: 'menu') => void): NavTab {
  return sheetTab('menu', 'Menu', <Menu className="size-5" aria-hidden />, sheet, open)
}

/** The glowing bot tab: starts a computer game. */
export function bossTab(
  game: GameId,
  bot: PlayerPublic,
  label: string,
  onSelect: () => void,
  disabled: boolean,
): NavTab {
  return {
    key: 'cpu',
    label,
    icon: <RobotFace className="size-6" palette={robotPaletteFor(bot.id)} />,
    tone: BOSS_THEME[game].arcade === 'drop' ? 'drop' : 'boss',
    onSelect,
    disabled,
  }
}

/**
 * Phone-only bottom tab bar for a game's home page. Renders an in-flow spacer so page content is
 * never hidden behind the fixed bar; hidden from `lg:` where the desktop cards take over.
 */
export function BottomNav({ tabs }: { tabs: NavTab[] }) {
  return (
    <>
      <div className="h-20 lg:hidden" aria-hidden />
      <nav className="fixed inset-x-0 bottom-0 z-30 lg:hidden" aria-label="Game actions">
        <div className="mx-auto max-w-md rounded-t-3xl bg-white/95 px-2 pt-2 pb-[max(env(safe-area-inset-bottom),0.5rem)] shadow-[0_-8px_24px_rgba(0,0,0,.08)] ring-1 ring-slate-200 backdrop-blur dark:bg-slate-900/95 dark:ring-slate-800">
          <ul className="grid grid-cols-4">
            {tabs.map((t) => (
              <li key={t.key}>
                <button
                  type="button"
                  onClick={() => {
                    feedback('tap')
                    t.onSelect()
                  }}
                  disabled={t.disabled}
                  aria-current={t.active ? 'page' : undefined}
                  className="flex w-full flex-col items-center gap-0.5 py-1 text-[11px] font-semibold text-slate-600 transition-transform active:scale-95 disabled:opacity-40 dark:text-slate-300"
                >
                  <span className={pillClass(t)}>{t.icon}</span>
                  {t.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </>
  )
}

function pillClass(t: NavTab): string {
  const base = 'flex h-8 w-14 items-center justify-center rounded-full transition-colors'
  if (t.tone === 'boss') {
    return `${base} bg-[linear-gradient(180deg,var(--arcade-boss-1),var(--arcade-boss-2))] text-white shadow-[0_0_16px_var(--robot-eye)]`
  }
  if (t.tone === 'drop') {
    return `${base} bg-[linear-gradient(180deg,var(--arcade-drop-1),var(--arcade-drop-2))] text-white shadow-[0_0_16px_var(--robot-c4-eye)]`
  }
  return t.active
    ? `${base} bg-slate-900 text-white dark:bg-white dark:text-slate-900`
    : `${base} bg-transparent`
}
